import type {
  ForemanCrewAssignment,
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  ForemanJobCrew,
} from '@ups/xplat/api/dto';

/** Crew member is on the crew only with an explicit row and `bActive === true`. */
export function countForemanEmployeesOnCrew(
  employees: ForemanEmployeeDispatch[],
  crewId: string
): number {
  if (!crewId) {
    return 0;
  }
  return employees.filter((e) => {
    const row = (e.foremanCrews || []).find(
      (c) => c.foremanJobCrewID === crewId
    );
    return row?.bActive === true;
  }).length;
}

/**
 * Equipment counts as on the crew when there is no assignment row for this crew
 * (implicitly on) or the row exists with `bActive === true`.
 */
export function countForemanEquipmentOnCrew(
  equipment: ForemanEquipmentDispatch[],
  crewId: string
): number {
  if (!crewId) {
    return 0;
  }
  return equipment.filter((e) => {
    const row = (e.foremanCrews || []).find(
      (c) => c.foremanJobCrewID === crewId
    );
    return !row || row.bActive === true;
  }).length;
}

/** One line: “{on} of {total} employees · {on} of {total} equipment on crew” (master summary + typeahead rows). */
export function formatForemanCrewCountsSummary(
  employeesOnCrew: number,
  employeesVisibleTotal: number,
  equipmentOnCrew: number,
  equipmentVisibleTotal: number
): string {
  return `${employeesOnCrew} of ${employeesVisibleTotal} employees · ${equipmentOnCrew} of ${equipmentVisibleTotal} equipment on crew`;
}

export function allForemanEmployeesOnCrew(
  employees: ForemanEmployeeDispatch[],
  crewId: string
): boolean {
  if (!crewId || employees.length === 0) {
    return false;
  }
  return employees.every((e) => {
    const row = (e.foremanCrews || []).find(
      (c) => c.foremanJobCrewID === crewId
    );
    return row?.bActive === true;
  });
}

export function allForemanEquipmentOnCrew(
  equipment: ForemanEquipmentDispatch[],
  crewId: string
): boolean {
  if (!crewId || equipment.length === 0) {
    return false;
  }
  return equipment.every((e) => {
    const row = (e.foremanCrews || []).find(
      (c) => c.foremanJobCrewID === crewId
    );
    return !row || row.bActive === true;
  });
}

/** Returns a new `foremanCrews` array with this crew row set to `bActive`, appending the row if missing. */
export function setForemanCrewRowActive(
  foremanCrews: ForemanCrewAssignment[] | undefined,
  crewId: string,
  bActive: boolean
): ForemanCrewAssignment[] {
  const crews = foremanCrews || [];
  const idx = crews.findIndex((c) => c.foremanJobCrewID === crewId);
  if (idx === -1) {
    return [
      ...crews.map((c) => ({ ...c })),
      { foremanJobCrewID: crewId, bActive },
    ];
  }
  return crews.map((c, i) => (i === idx ? { ...c, bActive } : { ...c }));
}

export function toggleForemanEmployeeCrewMembership(
  foremanCrews: ForemanCrewAssignment[] | undefined,
  crewId: string
): ForemanCrewAssignment[] {
  const crews = foremanCrews || [];
  const idx = crews.findIndex((c) => c.foremanJobCrewID === crewId);
  if (idx === -1) {
    return [
      ...crews.map((c) => ({ ...c })),
      { foremanJobCrewID: crewId, bActive: true },
    ];
  }
  const nextActive = !(crews[idx].bActive === true);
  return crews.map((c, i) =>
    i === idx ? { ...c, bActive: nextActive } : { ...c }
  );
}

/**
 * Active crews first, inactive last; alphabetical by name within each group
 * (case-insensitive). Does not mutate the input array.
 */
export function sortForemanCrewsForTypeahead(
  crews: ForemanJobCrew[]
): ForemanJobCrew[] {
  const inactiveRank = (c: ForemanJobCrew) => (c.bActive === false ? 1 : 0);
  return [...crews].sort((a, b) => {
    const byActive = inactiveRank(a) - inactiveRank(b);
    if (byActive !== 0) {
      return byActive;
    }
    return (a.name || '').localeCompare(b.name || '', undefined, {
      sensitivity: 'base',
    });
  });
}

export function toggleForemanEquipmentCrewMembership(
  foremanCrews: ForemanCrewAssignment[] | undefined,
  crewId: string
): ForemanCrewAssignment[] {
  const crews = foremanCrews || [];
  const idx = crews.findIndex((c) => c.foremanJobCrewID === crewId);
  if (idx === -1) {
    return [
      ...crews.map((c) => ({ ...c })),
      { foremanJobCrewID: crewId, bActive: false },
    ];
  }
  const nextActive = !(crews[idx].bActive === true);
  return crews.map((c, i) =>
    i === idx ? { ...c, bActive: nextActive } : { ...c }
  );
}
